import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
	Button,
	ButtonStack,
	DataTable,
	Drawer,
	ExportButton,
	FilterFields,
	LogoOverlay,
	PageHeading,
	PrintableReportHeader,
} from 'components';
import { useAppSelector, useGetChargeReconciliationQuery, useGetFlipRoomsQuery, useSystem } from 'store';
import { useFilters, useToast } from 'context';

import type { ChargeReconciliationResponse, FlipRoomsResponse } from 'store';
import { NumberLocale } from 'yup/lib/locale';

const test_data = {
	data: [
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Shimony, N., MD',
			specialty: 'Neurosurgery',
			case_id: '397013',
			vendor_name: 'DEPUY SYNTHES TRAUMA',
			item_description: 'SCREW CORTEX PELVIC SELF TAP 3.5X85',
			quantity: 10,
			unit_cost: 50.4,
			extended_amount: 504.4,
			usage_quantity: 1,
			delta: 9,
		}, // 1
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Shimony, N., MD',
			specialty: 'Neurosurgery',
			case_id: '397013',
			vendor_name: 'DEPUY SYNTHES TRAUMA',
			item_description: 'SCREW CORTEX PELVIC SELF TAP 3.5X70',
			quantity: 9,
			unit_cost: 50.4,
			extended_amount: 453.6,
			usage_quantity: 5,
			delta: 4,
		}, // 2
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Shimony, N., MD',
			specialty: 'Neurosurgery',
			case_id: '397013',
			vendor_name: 'SMITH & NEPHEW',
			item_description: 'STEM FEM PRESSFIT LEGION TI STR 14X120MM',
			quantity: 7,
			unit_cost: 1069.5,
			extended_amount: 7486.5,
			usage_quantity: 4,
			delta: 3,
		}, // 3
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Klimo, P., MD',
			specialty: 'Neurosurgery',
			case_id: '397312',
			vendor_name: 'STRYKER TRAUMA',
			item_description: 'SCREW CORT SELF TAP F-T HEX 3.5X32 NS',
			quantity: 6,
			unit_cost: 36,
			extended_amount: 216,
			usage_quantity: 1,
			delta: 5,
		}, // 4
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Klimo, P., MD',
			specialty: 'Neurosurgery',
			case_id: '397012',
			vendor_name: 'STRYKER TRAUMA',
			item_description: 'SCREW CORT SELF TAP F-T HEX 3.5X65 NS',
			quantity: 7,
			unit_cost: 36,
			extended_amount: 252,
			usage_quantity: 10,
			delta: -3,
		}, // 5
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Klimo, P., MD',
			specialty: 'Neurosurgery',
			case_id: '397012',
			vendor_name: 'STRYKER TRAUMA',
			item_description: 'SCREW CORT SELF TAP F-T HEX 3.5X70 NS',
			quantity: 1,
			unit_cost: 36,
			extended_amount: 36,
			usage_quantity: 5,
			delta: -4,
		}, // 6
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Klimo, P., MD',
			specialty: 'Neurosurgery',
			case_id: '397012',
			vendor_name: 'SMITH & NEPHEW',
			item_description: 'COMPONENT EX FIX R.E.D. II MULTI-DIR UPPER PART',
			quantity: 8,
			unit_cost: 2097.86,
			extended_amount: 16782.88,
			usage_quantity: 3,
			delta: 5,
		}, // 7
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Sawyer, J., MD',
			specialty: 'Orthopedic Surgery',
			case_id: '497431',
			vendor_name: 'STRYKER ORTHO',
			item_description: 'PIN POSITIONING HEADED NAIL 3/4',
			quantity: 1,
			unit_cost: 45.6,
			extended_amount: 45.6,
			usage_quantity: 7,
			delta: -6,
		}, // 8
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Orland, K., MD',
			specialty: 'Orthopedic Surgery',
			case_id: '397013',
			vendor_name: 'DEPUY ORTHO',
			item_description: 'HEAD METAL ARTICULEZE 36MM +5.0',
			quantity: 7,
			unit_cost: 475,
			extended_amount: 3325,
			usage_quantity: 5,
			delta: 2,
		}, // 9
		{
			requisition_date: '4/1/2026',
			surgeon_name: 'Sen, D., MD',
			specialty: 'Cardiothoracic Surgery',
			case_id: '332242',
			vendor_name: 'STRYKER SALES CMF',
			item_description: 'SCREW MXLFCL SLF-TAP X-PIN GLD 2.3X14 NS',
			quantity: 7,
			unit_cost: 57.42,
			extended_amount: 401.94,
			usage_quantity: 4,
			delta: -3,
		}, // 10
	],
};

export function ChargeReconciliation() {
	const { selectedFacility, selectedSystem } = useAppSelector((state) => state.userState);
	const { data: systemData } = useSystem();
	const facilities = systemData?.facilities;
	const currentFacility = facilities?.find((f) => f.id === selectedFacility);
	const system = systemData?.healthsystems.find((h) => h.id === selectedSystem);
	const [printing, setPrinting] = useState(false);
	const {
		dateRange,
		daysOfWeek,
		parLocations,
		manufacturers,
		resetFilters,
		applyFilters,
		clearFilters,
		filtersAreDirty,
		currentPageLoaded,
		metadata,
		filtersAreFetching,
	} = useFilters();
	const { createToast } = useToast();
	const [tableData, setTableData] = useState<ChargeReconciliationResponse['data']>([]);

	// There is sometimes a delay in our filters when a user switches pages
	// (which is why we check if currentPageLoaded is equal to our current page),
	// To account for the delay, we tell our RTK Query to skip until we set skipRequest to false.
	const [skipRequest, setSkipRequest] = useState(true);
	useEffect(() => {
		setTimeout(() => {
			if (currentPageLoaded === '/supply-reconciliation') {
				setSkipRequest(false);
			}
		}, 0);
	}, [currentPageLoaded]);

	const {
		data: intraop_data,
		error,
		isFetching,
	} = useGetChargeReconciliationQuery(
		{
			facility_id: selectedFacility,
			healthsystem_id: selectedSystem,
			filters: {
				days_of_week: daysOfWeek?.applied,
				start_date: format(dateRange?.applied.startDate, 'M/d/yyyy'),
				end_date: format(dateRange?.applied.endDate, 'M/d/yyyy'),
				par_locations: parLocations?.applied,
				manufacturers: manufacturers?.applied,
			},
		},
		{
			skip: skipRequest || filtersAreFetching,
		}
	);

	// When data from the endpoint changes or an error is encountered, update the
	// table data appropriately.
	useEffect(() => {
		// 	if (error) {
		// 		setTableData([]);

		// 		if ('data' in error) {
		// 			createToast({
		// 				title: `${error.data}`,
		// 				variant: 'error',
		// 			});
		// 		} else {
		// 			createToast({
		// 				title: 'There was an error connecting to the server.',
		// 				variant: 'error',
		// 			});
		// 		}

		// 		return;
		// 	}

		setTableData(intraop_data?.data ?? []);
	}, [intraop_data, createToast]);

	// Generate PDF from the current page, following the Analytics export approach.
	// If the captured report is taller than one PDF page, slice the same canvas
	// across as many PDF pages as needed instead of squeezing/clipping it.
	const generatePDF = async () => {
		const element = document.getElementById('overview');

		if (!element) {
			setPrinting(false);
			return;
		}

		try {
			const doc = new jsPDF();

			const html2canvasOptions = {
				useCORS: true,
				allowTaint: true,
				dpi: 200,
				scrollY: -window.scrollY,
			};

			const canvas = await html2canvas(element, html2canvasOptions);
			const pageWidth = doc.internal.pageSize.getWidth();
			const pageHeight = doc.internal.pageSize.getHeight();
			const margin = 10;
			const imageWidth = pageWidth - margin * 2;
			const imageHeight = (canvas.height * imageWidth) / canvas.width;
			const pageContentHeight = pageHeight - margin * 2;
			const pageCanvasHeight = (pageContentHeight * canvas.width) / imageWidth;
			let renderedHeight = 0;
			let pageIndex = 0;

			while (renderedHeight < canvas.height) {
				const sliceHeight = Math.min(pageCanvasHeight, canvas.height - renderedHeight);
				const pageCanvas = document.createElement('canvas');
				const pageContext = pageCanvas.getContext('2d');

				pageCanvas.width = canvas.width;
				pageCanvas.height = sliceHeight;

				if (!pageContext) {
					break;
				}

				pageContext.drawImage(canvas, 0, renderedHeight, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

				const imageData = pageCanvas.toDataURL('image/jpeg');
				const sliceImageHeight = Math.min(imageHeight - pageIndex * pageContentHeight, pageContentHeight);

				if (pageIndex > 0) {
					doc.addPage();
				}

				doc.addImage(imageData, 'JPEG', margin, margin, imageWidth, sliceImageHeight);

				renderedHeight += sliceHeight;
				pageIndex += 1;
			}

			doc.save(`flip_rooms_${currentFacility?.name ?? 'report'}.pdf`);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error generating PDF:', error); // keeping for easy error troubleshooting
		} finally {
			setPrinting(false);
		}
	};

	return (
		<div className='relative overflow-hidden' id='overview'>
			{printing && (
				<PrintableReportHeader>
					<p className='text-[2em] font-bold leading-tight text-bluegray-900'>Charge Reconciliation</p>
					<p className='mt-2 text-[1.35em] font-medium text-bluegray-500'>{`${format(
						dateRange.applied.startDate,
						'M/d/yyyy'
					)} – ${format(dateRange.applied.endDate, 'M/d/yyyy')}`}</p>
					<p className='mt-1 text-[1.1em] font-medium text-bluegray-500'>{`${
						currentFacility?.intraop_facility_name_alias ? currentFacility.intraop_facility_name_alias : currentFacility?.name
					} - ${system?.name}`}</p>
				</PrintableReportHeader>
			)}
			{!printing && <PageHeading>Charge Reconciliation</PageHeading>}

			{!printing && (
				<div className='flex justify-end gap-2 pb-3 overflow-auto'>
					<Drawer
						metadata={metadata}
						filtersAreDirty={filtersAreDirty}
						trigger={
							<Button sizeX='sm' sizeY='md' variant={'primary-ghost'} className='mr-2'>
								<span className='material-symbol'>filter_alt</span>
								Filters
							</Button>
						}
						quickActions={[
							{
								icon: 'undo',
								onClick: resetFilters,
								tooltipText: 'Discard unapplied filter changes',
								disabled: !filtersAreDirty,
							},
							{
								icon: 'restart_alt',
								onClick: clearFilters,
								tooltipText: 'Reset filters to default',
								disabled: !metadata.saved_at,
							},
						]}
						actionButtons={[
							{
								onClick: applyFilters,
								children: 'Apply',
								disabled: !filtersAreDirty,
							},
							{
								// eslint-disable-next-line @typescript-eslint/no-empty-function
								onClick: () => {},
								children: 'Views',
								disabled: false,
							},
						]}
					>
						<FilterFields
							fields={[
								'dateRange',
								'daysOfWeek',
								'serviceLines',
								'rooms',
								'primetime',
								'addOns',
								'encounterTypes',
								'procedures',
							]}
						/>
					</Drawer>
					<ButtonStack>
						<Button sizeX='md' sizeY='md' variant='primary'>
							<ExportButton no_button={true} sizeX='md' sizeY='md' variant='primary' contents={intraop_data?.data ?? []}>
								Export CSV
							</ExportButton>
						</Button>

						<Button
							sizeX='md'
							sizeY='md'
							variant='primary'
							onClick={() => {
								setPrinting(true);
								setTimeout(() => {
									generatePDF();
								}, 0);
							}}
						>
							Export PDF
						</Button>
					</ButtonStack>
				</div>
			)}

			{isFetching || skipRequest ? (
				<LogoOverlay backgroundColor={'transparent'} />
			) : (
				<DataTable
					title='Charge Details by Case'
					tooltipContent={`	
					Use this table to view surgeons with overlapping or concurrent cases. In general, judicious use of flip rooms takes into account overall schedule efficiency and the opportunity cost of potential downtime in a room reserved for a flipped case.`}
					goToHelpID={'fliprooms'}
					headerContentRight={
						!printing ? (
							<div className='inline-flex items-center justify-center relative drop-shadow border border-blue-500 rounded text-blue-900 w-11/12'>
								<span className='text-b3 font-semibold py-4 px-6'>
									{`${format(dateRange.selected.startDate, 'M/d/yyyy')} – ${format(dateRange.selected.endDate, 'M/d/yyyy')}`}
								</span>
							</div>
						) : undefined
					}
					headerContentCenterAlignment='left'
					disablePagination={printing}
					disableRowCounter={printing}
					columns={[
						{
							header: 'Req. Date',
							accessorKey: 'requisition_date',
							cell: ({ row }) =>
								`${row.original.requisition_date ? new Date(row.original.requisition_date).toLocaleDateString('en-US') : ''}`,
						},
						{ header: 'Req. ID', accessorKey: 'requisition_number', enableGlobalFilter: false },
						{ header: 'Req. Type', accessorKey: 'requisition_type', enableGlobalFilter: false },
						{ header: 'Surgeon', accessorKey: 'surgeon_name' },
						{ header: 'Specialty', accessorKey: 'service_line', enableGlobalFilter: false },
						{ header: 'Case ID', accessorKey: 'case_number', enableGlobalFilter: false },
						{
							header: 'Manufacturer',
							accessorKey: 'manufacturer',
						},
						{
							header: 'Item ID',
							accessorKey: 'raw_item_id',
						},
						{
							header: 'Item Desc.',
							accessorKey: 'item_description',
						},
						{
							header: 'Order Qty',
							accessorKey: 'num_ordered_at_lowest_unit_of_measure',
						},
						{
							header: 'Unit Cost',
							accessorKey: 'unit_cost',
							enableGlobalFilter: false,
							cell: ({ row }) => `$${row.original.unit_cost.toLocaleString()}`,
						},
						{
							header: 'Ext. Amount',
							accessorKey: 'extended_amount',
							enableGlobalFilter: false,
							cell: ({ row }) => `$${row.original.extended_amount.toLocaleString()}`,
						},
						{ header: 'Used Qty', accessorKey: 'number_used', enableGlobalFilter: false },
						{
							header: 'Delta',
							accessorKey: 'delta',
							enableGlobalFilter: false,
							cell: ({ row }) => {
								let color =
									row.original.delta > 0 ? 'font-bold text-blue-500 text-center' : 'font-bold text-blue-800 text-center ';

								const isEqualToZero = row.original.delta === 0;

								if (isEqualToZero) {
									color = 'text-gray-300 text-center';
								}
								return <p className={color}>{row.original.delta}</p>;
							},
							// meta: {
							// 	bodyClass: util_target_met ? 'bg-green-50' : 'bg-red-50',
							// 	footerClass: util_target_met ? 'bg-green-50' : 'bg-red-50',
							// 	headerClass: util_target_met ? 'bg-green-50 text-center pl-3' : 'bg-red-50 text-center pl-3',
							// },
						},
					]}
					data={tableData}
				/>
			)}
		</div>
	);
}

export default ChargeReconciliation;
